const DEFAULT_TTL_MS = 15000;
const MAX_ENTRIES = 150;

const responseCache = new Map();
const inFlight = new Map();

// Bumped by every clearGetRequestCache(). A request that started before a clear (for example a Billing list fetch still running when a
// payment is posted) carries the old generation: it is never joined by later callers and never written to the cache, so it cannot
// bring pre-write data back after the write.
let generation = 0;

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === "object") {
    return Object.keys(value).sort().reduce((result, key) => {
      result[key] = stableValue(value[key]);
      return result;
    }, {});
  }
  return value;
}

export function getRequestCacheKey(url, config = {}, authKey = "") {
  return JSON.stringify({
    authKey,
    url: String(url || ""),
    params: stableValue(config.params || {}),
    responseType: config.responseType || "json",
  });
}

export function clearGetRequestCache() {
  generation += 1;
  responseCache.clear();
  inFlight.clear();
}

export function cachedGet({ url, config = {}, authKey = "", request }) {
  const ttlMs = Math.max(0, Number(config.cacheTtlMs ?? DEFAULT_TTL_MS));
  if (config.cache === false || ttlMs === 0 || config.responseType === "blob") return request();

  const key = getRequestCacheKey(url, config, authKey);
  const now = Date.now();
  const cached = responseCache.get(key);
  if (!config.forceRefresh && cached && now - cached.storedAt <= ttlMs) {
    return Promise.resolve(cached.response);
  }
  if (!config.forceRefresh && inFlight.has(key)) return inFlight.get(key);

  const startedInGeneration = generation;
  const pending = request()
    .then((response) => {
      if (startedInGeneration !== generation) return response; // the cache was cleared while this request was running: do not store it
      responseCache.delete(key);
      responseCache.set(key, { storedAt: Date.now(), response });
      while (responseCache.size > MAX_ENTRIES) responseCache.delete(responseCache.keys().next().value);
      return response;
    })
    .finally(() => {
      if (inFlight.get(key) === pending) inFlight.delete(key);
    });
  inFlight.set(key, pending);
  return pending;
}

export function getRequestCacheStats() {
  return { cached: responseCache.size, inFlight: inFlight.size };
}
